import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IUserCart } from '../../models/user-cart.model';
import { ApiService } from '../../services/api.service';
import { AddToCartModel } from 'src/app/site/feature-modules/home/shared/models/user-cart.models';
import { ApiResponse } from '../../models/api-response.model';
import { AddToCart, ClearCart, DeleteCartItem, GetCart, ProcessCart, UpdateCart } from './cart.action';
import { Injectable } from '@angular/core';
import { CartBlService } from 'src/app/site/feature-modules/home/shared/services/cart-bl.service';

export interface CartStateModel {
  cart: IUserCart | null;
}

@State<CartStateModel>({
  name: 'cart',
  defaults: {
    cart: null
  }
})
@Injectable()
export class CartState {
  constructor(
    private apiService: ApiService,
    private cartBlService:CartBlService
  ) { }

  // Selector to get the current cart
  @Selector()
  static cart(state: CartStateModel): IUserCart | null {
    return state.cart;
  }

  // Private method to process and format cart data
  private processCart(cart: IUserCart | null): IUserCart | null {
    if (cart) {
      cart.remainingToPay = Math.round(cart.remainingToPay * 100) / 100;
      cart.totalPriceWithTax = Math.round(cart.totalPriceWithTax * 100) / 100;
    }
    return cart;
  }

  @Action(GetCart)
  getCart(ctx: StateContext<CartStateModel>): Observable<any> {
    return this.apiService.get<ApiResponse<IUserCart>>('/cart', {}).pipe(
      tap((res) => {
        ctx.dispatch(new ProcessCart(res.data));
      }),
      catchError((error) => {
        this.cartBlService.handleErrors(error);
        return throwError(() => error);
      })
    );
  }

  @Action(AddToCart)
  addToCart(ctx: StateContext<CartStateModel>, action: AddToCart): Observable<any> {
    return this.apiService.post<AddToCartModel, ApiResponse<IUserCart>>('/cart', action.payload).pipe(
      tap((res) => {
        ctx.dispatch(new GetCart);
      }),
      catchError((error) => {
        this.cartBlService.handleErrors(error);
        return throwError(() => error);
      })
    );
  }

  @Action(DeleteCartItem)
  deleteCartItem(ctx: StateContext<CartStateModel>, action: DeleteCartItem): Observable<any> {
    return this.apiService.delete<number, ApiResponse<IUserCart>>(`/cart/${action.itemId}`).pipe(
      tap((res) => {
        ctx.dispatch(new GetCart);
      }),
      catchError((error) => {
        this.cartBlService.handleErrors(error);
        return throwError(() => error);
      })
    );
  }

  @Action(ClearCart)
  clearCart(ctx: StateContext<CartStateModel>): Observable<any> {
    return this.apiService.delete<null, ApiResponse<IUserCart>>('/cart').pipe(
      tap((res) => {
        ctx.dispatch(new GetCart);
      }),
      catchError((error) => {
        this.cartBlService.handleErrors(error);
        return throwError(() => error);
      })
    );
  }

  @Action(ProcessCart)
  processCartAction(ctx: StateContext<CartStateModel>, action: ProcessCart) {
    const processedCart = this.processCart(action.payload);
    ctx.patchState({ cart: processedCart });
  }

  // Action to update the cart
  @Action(UpdateCart)
  updateCart(ctx: StateContext<CartStateModel>, action: UpdateCart) {
    // Ensure the cart object is properly updated (immutable update)
    const updatedCart = { ...action.updatedCart };

    // You can also apply any further processing here if necessary
    updatedCart.remainingToPay = Math.round(updatedCart.remainingToPay * 100) / 100;
    updatedCart.totalPriceWithTax = Math.round(updatedCart.totalPriceWithTax * 100) / 100;

    // Update the state with the new cart
    ctx.patchState({ cart: updatedCart });
  }
}
